import { STORAGE_KEY } from "@constants";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { Language } from "@types";
import { PropsWithChildren, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface MemberSettingsLoaderProps extends PropsWithChildren {}

/**
 * Check if member settings are loaded
 * If loaded, show app
 */
const MemberSettingsLoader = ({ children }: MemberSettingsLoaderProps) => {
  const { isLoaded, language, languageList } = useMemberSettings();
  const { i18n } = useTranslation();

  // Initialize language settings once on component mount
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        let currentLanguage = localStorage.getItem(STORAGE_KEY.LANGUAGE);
        let shouldChangeLanguage = false;

        // If no language is stored, use server-configured language
        if (!currentLanguage) {
          currentLanguage = language;
          shouldChangeLanguage = true;
        }

        // If current language is not in the available list,
        // try server language if it's in the list, otherwise use first available
        if (!languageList?.includes(currentLanguage)) {
          currentLanguage = languageList?.includes(language) ? language : languageList?.[0];
          shouldChangeLanguage = true;
        }

        if (shouldChangeLanguage && currentLanguage) {
          i18n?.changeLanguage(currentLanguage as Language);
        }
      } catch (error) {
        console.error("Failed to initialize language:", error);
        // On error, try server language if it's in the list, otherwise first available
        const fallbackLang = languageList?.includes(language) ? language : languageList?.[0];
        if (fallbackLang) {
          i18n?.changeLanguage(fallbackLang as Language);
        }
      }
    };

    if (isLoaded) {
      initializeLanguage();
    }
  }, [language, languageList, i18n, isLoaded]);

  if (!isLoaded) {
    return null;
  }

  return children;
};

export default MemberSettingsLoader;
